import { PrimaryBigButton } from 'components/ReusableComponents/Button/BigButtons'
// import DragAndDropUploader from 'components/ReusableComponents/DragAndDropUploader'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import { LatestVersionLink } from 'components/ReusableComponents/LatestVersionLink'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { Calendar } from 'react-multi-date-picker'
import { useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import pepApi from 'services/api/pep'
import { collectMissingReportDates } from 'utils/collectMissingReportDates'
import { isRunningInElectron } from 'utils/envHelpers'

import styles from './PMSReports.module.scss'
//import { pepConfig } from './temp-pep-config'

export function PMSReports() {
  const dateSince = moment().subtract(6, 'years').startOf('day')
  const today = moment().format('YYYY-MM-DD')

  const { addToast } = useToasts()

  const [activity, setActivity] = useState([])
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()

  const sessionId = useSelector(s => s.login.sessionId)
  const subsidiaryId = useSelector(state => state.login.currentHotelId)

  const getData = React.useCallback(() => {
    pepApi
      .getActivity()
      .then(res => {
        setActivity(convertToDatesArray(res.activities))
        setStartDate(moment(res.startDate))
        setEndDate(moment(res.endDate))
      })
      .catch(() => {
        addToast('Something went wrong', {
          appearance: 'error',
          autoDismiss: true
        })
      })
  }, [addToast])

  const handleClickLoginToPepButton = useCallback(() => {
    if (window?.electron?.postMessage) {
      window.electron.postMessage({
        type: 'test-login',
        data: {
          subsidiaryId
        }
      })
    }
  }, [subsidiaryId])

  // Send message to Electron app when component mounts
  useEffect(() => {
    if (window?.electron?.postMessage) {
      window.electron.postMessage({
        type: 'pms-reports-page-visited',
        data: {
          timestamp: new Date().toISOString(),
          sessionId
        }
      })
    }
  }, [sessionId])

  useEffect(() => {
    getData()
  }, [getData])

  const missingDates = React.useMemo(
    () =>
      collectMissingReportDates(dateSince, today).filter(
        date => !activity.includes(date)
      ),
    [activity, today, dateSince]
  )

  return (
    <div className={styles.container}>
      <CustomHeader width={1180} title="PMS Reports" />
      <div className={styles.left}>
        {missingDates.length > 0 ? (
          <div className={styles.warningCard}>
            {activity.length > 0 ? (
              <>Reports between {convertToPeriodFormat(missingDates)} are missing</>
            ) : (
              <>No report updated</>
            )}
          </div>
        ) : (
          <div className={styles.successCard}>All reports are uploaded</div>
        )}

        {isRunningInElectron() && (
          <>
            <PrimaryBigButton onClick={handleClickLoginToPepButton}>
              Login to PEP
            </PrimaryBigButton>
          </>
        )}
        {!isRunningInElectron() && (
          <div className={styles.latestVersionLink}>
            <p className={styles.latestVersionLinkText}>
              Download Directful app for Hilton to integrate with PEP
            </p>
            <LatestVersionLink />
          </div>
        )}
      </div>
      <div className={styles.right}>
        <Calendar
          className={styles.calendar}
          numberOfMonths={2}
          shadow={false}
          mapDays={({ date }) => {
            const isActivity = activity.includes(date.format('YYYY-MM-DD'))
            const isDisabled =
              moment(date.format('YYYY-MM-DD')).isBefore(startDate) ||
              moment(date.format('YYYY-MM-DD')).isAfter(endDate)

            let props = {}

            props.style = {
              borderRadius: 0,
              background: isDisabled ? 'white' : isActivity ? '#66BB6A' : '#F76060',
              color: isDisabled ? 'var(--grayLighter)' : 'white',
              margin: -4
            }

            return props
          }}
        />
      </div>
    </div>
  )
}

function convertToDatesArray(data) {
  const result = []

  for (const item of data) {
    const dates = collectMissingReportDates(item.startDate, item.endDate)
    result.push(...dates)
  }

  return result
}

function formatDate(date) {
  return moment(date).format('MMM D YYYY')
}

function formatPeriodDate(start, end) {
  return `${moment(start).format('MMM D')} to ${moment(end).format('MMM D YYYY')}`
}

function convertToPeriodFormat(dates) {
  const sortedDates = dates.sort()
  const formattedDates = []
  let start = moment(sortedDates[0])
  let end = moment(sortedDates[0])

  for (let i = 1; i < sortedDates.length; i++) {
    const currentDate = moment(sortedDates[i])
    const nextDate = moment(sortedDates[i - 1]).add(1, 'day')

    if (currentDate.isSame(nextDate, 'day')) {
      end = currentDate
    } else {
      if (start.isSame(end, 'day')) {
        formattedDates.push(formatDate(start))
      } else {
        formattedDates.push(formatPeriodDate(start, end))
      }
      start = currentDate
      end = currentDate
    }
  }

  if (start.isSame(end, 'day')) {
    formattedDates.push(formatDate(start))
  } else {
    formattedDates.push(`${formatDate(start)} to ${formatDate(end)}`)
  }

  return formattedDates.join(', ')
}
